import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { Moon, Search, Eye, EyeOff, Check, X, AlertTriangle, KeyRound, ShieldCheck, UserPlus } from 'lucide-react';
import DriverAvatar, { fetchDriverPhotos } from '../components/DriverPhoto';
import SorterTaxSuperRequest from '../components/SorterTaxSuperRequest';

// Night-shift ops directory — the personnel roster for the night sort. It's the sorter
// equivalent of the Drivers page: core fields are visible to all site users; PIN and
// bank/super are revealed on demand and only for admins. The registry is the Supabase
// `sorters` table via sorter-registry-sb (same siteLogin gate the Drivers page uses on
// drivers-sb); bank/super are redacted from the list feed and live behind the vault
// admin secret (sorter-vault revealRegistry), so revealing them is audited.
const API = '/.netlify/functions/sorter-registry-sb';
const post = (body) => fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then((r) => r.json());

const COLS = [
  { key: 'login', label: 'Login' },
  { key: 'name', label: 'Name' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'email', label: 'Email' },
  { key: 'employment', label: 'Employment' },
  { key: 'classification', label: 'Role' },
  { key: 'facility', label: 'Facility' },
  { key: 'permanentNight', label: 'Nights' },
  { key: 'active', label: 'Active' },
];

const th = { textAlign: 'left', padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: '.66rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--hp-gold)', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', borderBottom: '1px solid var(--hp-border)' };
const td = { padding: '9px 12px', fontSize: '.85rem', color: 'var(--hp-text)', borderBottom: '1px solid var(--hp-border)', whiteSpace: 'nowrap' };

const fieldStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontFamily: 'var(--font-mono)', fontSize: '.9rem', letterSpacing: '.04em' };
const labelStyle = { fontFamily: 'var(--font-mono)', fontSize: '.62rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--hp-dim)', marginBottom: 5, display: 'block' };

// The list feed is sheet-shaped ({ Login, Active:'Y'/'N', PIN, Name, ... }) with super/bank
// blanked — map it to the flat row this page renders.
function mapSorter(s) {
  return {
    login: s.Login || '',
    name: s.Name || '',
    mobile: s.Mobile || '',
    email: s.Email || '',
    employment: s['Employment Type'] || '',
    grade: s.Grade || '',
    classification: s.Classification || '',
    facility: s.Facility || '',
    baseRate: s['Base Rate'] || '',
    startDate: s['Start Date'] || '',
    address: s.Address || '',
    dob: s.DOB || '',
    award: s.Award || '',
    emergencyName: s['Emergency Name'] || '',
    emergencyPhone: s['Emergency Phone'] || '',
    tfnStatus: s['TFN Status'] || '',
    superDeclStatus: s['Super Decl Status'] || '',
    permanentNight: (s['Permanent Night'] || 'Y') !== 'N',
    active: (s.Active || 'Y') !== 'N',
    pin: s.PIN || '',
  };
}

// ── Reveal panel (admin) — PIN is already in the loaded feed so it shows instantly;
// bank/super are secret-gated through the vault (revealRegistry) and auto-hide after 30s,
// mirroring the Night Sorters hub. ────────────────────────────────────────────────────
function RevealPanel({ sorter, colSpan, by }) {
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState('');

  async function revealBank() {
    setBusy(true); setErr('');
    try {
      const r = await fetch('/.netlify/functions/sorter-vault', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Hp-Vault-Admin': secret },
        body: JSON.stringify({ op: 'revealRegistry', login: sorter.login, by: 'admin' }),
      }).then((x) => x.json());
      if (r && r.success) { setDetail(r.detail || {}); setSecret(''); setOpen(false); setTimeout(() => setDetail(null), 30000); }
      else setErr(r && r.error === 'Not authorised.'
        ? 'Reveal needs the vault admin secret (HP_VAULT_ADMIN_SECRET) — entered correctly.'
        : ((r && r.error) || 'Could not reveal bank & super details.'));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  const cell = (k, v) => (
    <div key={k}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.62rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--hp-dim)' }}>{k}</div>
      <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-text)', fontWeight: 700 }}>{v || '—'}</div>
    </div>
  );
  const miniBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', fontSize: '.76rem' };

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: '10px 14px', background: 'var(--hp-bg)', borderBottom: '1px solid var(--hp-border)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, fontSize: '.8rem', marginBottom: 6 }}>
          {[['PIN', sorter.pin], ['Employment', sorter.employment], ['Award', sorter.award], ['Base rate', sorter.baseRate], ['Start', sorter.startDate], ['DOB', sorter.dob], ['TFN', sorter.tfnStatus], ['Super decl.', sorter.superDeclStatus], ['Emergency', sorter.emergencyName && `${sorter.emergencyName} · ${sorter.emergencyPhone}`]]
            .filter(([, v]) => v !== '' && v !== undefined && v !== null)
            .map(([k, v]) => cell(k, v))}
        </div>

        {/* Chase the paperwork the sorter has to complete themselves. Capped in width:
            the expanded row spans a very wide table, and a right-aligned button out at
            the far edge of it needs a horizontal scroll to reach. */}
        <div style={{ marginTop: 8, maxWidth: 720 }}>
          <SorterTaxSuperRequest login={sorter.login} name={sorter.name} by={by} />
        </div>

        <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'rgba(201,168,76,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <ShieldCheck size={14} color="var(--hp-gold)" />
            <span style={{ fontWeight: 700, fontSize: '.84rem' }}>Bank &amp; super</span>
            {!detail && !open && <button onClick={() => setOpen(true)} style={{ ...miniBtn, marginLeft: 'auto' }}><Eye size={13} /> Reveal</button>}
          </div>
          {!detail && !open && <div style={{ color: 'var(--hp-dim)', fontSize: '.7rem', marginTop: 6 }}>Hidden by default. Revealing is logged to the vault audit and needs <code>HP_VAULT_ADMIN_SECRET</code>.</div>}
          {open && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Vault admin secret" autoComplete="off"
                style={{ ...fieldStyle, maxWidth: 220, padding: '6px 9px' }} />
              <button onClick={revealBank} disabled={busy || !secret} style={{ ...miniBtn, background: 'var(--hp-gold)', color: '#1a1a2e', borderColor: 'transparent', opacity: (busy || !secret) ? 0.5 : 1 }}>{busy ? '…' : 'Show details'}</button>
              <button onClick={() => { setOpen(false); setSecret(''); setErr(''); }} style={miniBtn}>Cancel</button>
            </div>
          )}
          {err && <div style={{ color: '#ef4444', fontSize: '.76rem', marginTop: 7 }}>{err}</div>}
          {detail && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, fontSize: '.8rem', marginTop: 10 }}>
              {cell('Super fund', detail.superFund)}
              {cell('Super USI', detail.superUsi)}
              {cell('Member no.', detail.superMemberNo)}
              {cell('Account name', detail.accountName)}
              {cell('BSB', detail.bsb)}
              {cell('Account no.', detail.accNumber)}
              <div style={{ flexBasis: '100%', fontSize: '.68rem', color: 'var(--hp-dim)' }}>Hidden again shortly · this view is logged to the vault audit.</div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Edit modal (admin) — PIN + contact/employment. Note there is no login rename here:
// the sorter login is a join key across the night-sort tables with no server-side rename
// path (unlike drivers), so it's read-only. ─────────────────────────────────────────────
const EMP_OPTIONS = ['Casual', 'Part Time', 'Full Time'];
const ROLE_OPTIONS = ['Sorter', 'Team Leader'];

function EditModal({ sorter, onClose, onSaved }) {
  const [f, setF] = useState({
    name: sorter.name || '', mobile: sorter.mobile || '', email: sorter.email || '',
    employmentType: sorter.employment || 'Casual', classification: sorter.classification || 'Sorter',
    grade: sorter.grade || '', facility: sorter.facility || '', baseRate: sorter.baseRate || '',
    startDate: sorter.startDate || '', emergencyName: sorter.emergencyName || '', emergencyPhone: sorter.emergencyPhone || '',
    pin: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  async function save() {
    setErr(''); setSaving(true);
    try {
      const updates = { login: sorter.login };
      ['name', 'mobile', 'email', 'employmentType', 'classification', 'grade', 'facility', 'baseRate', 'startDate', 'emergencyName', 'emergencyPhone'].forEach((k) => { updates[k] = f[k]; });
      if (f.pin.trim()) updates.pin = f.pin.trim();
      const res = await post({ action: 'updateSorter', sorter: updates });
      if (!res.success) throw new Error(res.error || 'Could not save');
      onSaved(sorter.login, {
        name: f.name, mobile: f.mobile, email: f.email, employment: f.employmentType,
        classification: f.classification, grade: f.grade, facility: f.facility, baseRate: f.baseRate,
        startDate: f.startDate, emergencyName: f.emergencyName, emergencyPhone: f.emergencyPhone,
        ...(f.pin.trim() ? { pin: f.pin.trim() } : {}),
      });
      onClose();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  const field = (label, k, opts) => (
    <div>
      <label style={labelStyle}>{label}</label>
      {opts
        ? <select value={f[k]} onChange={set(k)} style={fieldStyle}>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>
        : <input value={f[k]} onChange={set(k)} style={fieldStyle} />}
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 22, width: '100%', maxWidth: 560, maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <KeyRound size={17} color="var(--hp-gold)" />
          <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '1.05rem' }}>Edit night-shift record</h3>
        </div>
        <div style={{ color: 'var(--hp-dim)', fontSize: '.8rem', marginBottom: 18 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--hp-text)' }}>{sorter.login}</span> · login is fixed
        </div>

        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {field('Name', 'name')}
          {field('Mobile', 'mobile')}
          {field('Email', 'email')}
          {field('Facility', 'facility')}
          {field('Employment', 'employmentType', EMP_OPTIONS)}
          {field('Role', 'classification', ROLE_OPTIONS)}
          {field('Grade', 'grade')}
          {field('Base rate', 'baseRate')}
          {field('Start date', 'startDate')}
          {field('Emergency name', 'emergencyName')}
          {field('Emergency phone', 'emergencyPhone')}
          <div>
            <label style={labelStyle}>PIN (blank = keep)</label>
            <input value={f.pin} onChange={(e) => setF((s) => ({ ...s, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))} placeholder="••••••" style={fieldStyle} />
          </div>
        </div>

        {err && <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.4)', color: '#ef4444', borderRadius: 8, padding: '9px 12px', margin: '14px 0 0', fontSize: '.8rem' }}>{err}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={onClose} disabled={saving} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-dim)', cursor: 'pointer', fontSize: '.85rem' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: saving ? 'var(--hp-border)' : 'var(--hp-gold)', color: saving ? 'var(--hp-dim)' : '#000', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: '.85rem' }}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Add modal (admin) — creates a new sorter row (addSorter). A blank PIN is
// server-generated and returned so the office can hand it over. ──────────────────────────
function AddModal({ onClose, onAdded }) {
  const [f, setF] = useState({ login: '', name: '', mobile: '', email: '', employmentType: 'Casual', classification: 'Sorter', facility: '', baseRate: '', startDate: '', pin: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const login = f.login.trim().toUpperCase();
  const loginValid = /^[A-Z0-9]{3,20}$/.test(login);

  async function save() {
    setErr(''); setSaving(true);
    try {
      const res = await post({ action: 'addSorter', sorter: { ...f, login } });
      if (!res.success) throw new Error(res.error || 'Could not add');
      setDone(`Added ${login} · PIN ${res.pin}`);
      onAdded();
      setTimeout(onClose, 1600);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  const field = (label, k, opts) => (
    <div>
      <label style={labelStyle}>{label}</label>
      {opts
        ? <select value={f[k]} onChange={set(k)} style={fieldStyle}>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>
        : <input value={f[k]} onChange={set(k)} style={fieldStyle} />}
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 22, width: '100%', maxWidth: 560, maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <UserPlus size={17} color="var(--hp-gold)" />
          <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '1.05rem' }}>Add night-shift person</h3>
        </div>

        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}>Login *</label>
            <input autoFocus value={f.login} onChange={(e) => setF((s) => ({ ...s, login: e.target.value.toUpperCase() }))} style={fieldStyle} />
          </div>
          {field('Name', 'name')}
          {field('Mobile', 'mobile')}
          {field('Email', 'email')}
          {field('Facility', 'facility')}
          {field('Employment', 'employmentType', EMP_OPTIONS)}
          {field('Role', 'classification', ROLE_OPTIONS)}
          {field('Base rate', 'baseRate')}
          {field('Start date', 'startDate')}
          <div>
            <label style={labelStyle}>PIN (blank = auto)</label>
            <input value={f.pin} onChange={(e) => setF((s) => ({ ...s, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))} placeholder="auto-generate" style={fieldStyle} />
          </div>
        </div>

        {f.login && !loginValid && <div style={{ fontSize: '.78rem', color: 'var(--hp-amber)', marginTop: 12 }}>Login must be 3–20 characters, letters and numbers only.</div>}
        {err && <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.4)', color: '#ef4444', borderRadius: 8, padding: '9px 12px', margin: '14px 0 0', fontSize: '.8rem' }}>{err}</div>}
        {done && <div style={{ background: 'rgba(31,138,76,.12)', border: '1px solid rgba(31,138,76,.4)', color: 'var(--hp-green)', borderRadius: 8, padding: '9px 12px', margin: '14px 0 0', fontSize: '.8rem' }}>{done}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={onClose} disabled={saving} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-dim)', cursor: 'pointer', fontSize: '.85rem' }}>Cancel</button>
          <button onClick={save} disabled={!loginValid || saving || !!done} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: (loginValid && !saving && !done) ? 'var(--hp-gold)' : 'var(--hp-border)', color: (loginValid && !saving && !done) ? '#000' : 'var(--hp-dim)', fontWeight: 700, cursor: (loginValid && !saving && !done) ? 'pointer' : 'not-allowed', fontSize: '.85rem' }}>{saving ? 'Adding…' : 'Add'}</button>
        </div>
      </div>
    </div>
  );
}

export default function NightShiftPage() {
  const { currentUser } = useApp();
  const isAdmin = currentUser?.role === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'login', dir: 1 });
  const [busy, setBusy] = useState({});          // login -> true while a row write is in flight
  const [reveal, setReveal] = useState({});      // login -> true (admin reveal panel open)
  const [editing, setEditing] = useState(null);  // sorter row being edited
  const [adding, setAdding] = useState(false);
  const [photos, setPhotos] = useState({});      // login -> profile pic data URL

  const load = useCallback(() => {
    setLoading(true); setError('');
    post({ action: 'listSorters' })
      .then((d) => { if (!d.success) throw new Error(d.error || 'Could not load night-shift staff'); setRows((d.sorters || []).map(mapSorter)); })
      .catch((e) => setError(e.message || 'Could not load night-shift staff'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (rows.length) fetchDriverPhotos(rows.map((r) => r.login)).then((p) => setPhotos((prev) => ({ ...p, ...prev })));
  }, [rows]);

  const patchRow = (login, patch) => setRows((rs) => rs.map((r) => (r.login === login ? { ...r, ...patch } : r)));
  const setRowBusy = (login, v) => setBusy((b) => ({ ...b, [login]: v }));

  async function toggleActive(login, next) {
    patchRow(login, { active: next });    // optimistic
    setRowBusy(login, true);
    try {
      const res = await post({ action: 'updateSorter', sorter: { login, active: next ? 'Y' : 'N' } });
      if (!res.success) throw new Error(res.error);
    } catch (e) {
      patchRow(login, { active: !next }); // revert
      setError(`Could not update ${login}: ${e.message}`);
    } finally { setRowBusy(login, false); }
  }

  function toggleReveal(login) {
    setReveal((r) => { const n = { ...r }; if (n[login]) delete n[login]; else n[login] = true; return n; });
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = rows;
    if (q) out = rows.filter((r) => [r.login, r.name, r.mobile, r.email, r.facility, r.classification].some((v) => (v || '').toLowerCase().includes(q)));
    const { key, dir } = sort;
    return [...out].sort((a, b) => {
      let av = a[key], bv = b[key];
      if (key === 'active' || key === 'permanentNight') { av = av ? 1 : 0; bv = bv ? 1 : 0; }
      av = (av === undefined || av === null) ? '' : av; bv = (bv === undefined || bv === null) ? '' : bv;
      if (av < bv) return -1 * dir; if (av > bv) return 1 * dir; return 0;
    });
  }, [rows, query, sort]);

  const clickSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const activeCount = rows.filter((r) => r.active).length;
  const nightsCount = rows.filter((r) => r.permanentNight).length;
  const extraCols = isAdmin ? 2 : 0;   // Reveal + Edit columns

  return (
    <div style={{ padding: '20px', maxWidth: 1280, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Moon size={22} color="var(--hp-gold)" />
        <h2 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Night Shift</h2>
      </div>
      <div style={{ color: 'var(--hp-dim)', fontSize: '.82rem', marginBottom: 16 }}>
        {rows.length} night-shift staff · {activeCount} active · {nightsCount} on permanent nights
        {isAdmin ? ' · you can reveal PIN + bank/super' : ' · PIN/bank hidden (admin only)'}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '8px 12px', flex: '1 1 320px', maxWidth: 380 }}>
          <Search size={16} color="var(--hp-dim)" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search login, name, mobile, facility…"
            style={{ border: 'none', outline: 'none', background: 'transparent', color: 'var(--hp-text)', fontSize: '.9rem', width: '100%', fontFamily: 'var(--font-body)' }} />
        </div>
        {isAdmin && (
          <button onClick={() => setAdding(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 'var(--radius)', border: 'none', background: 'var(--hp-gold)', color: '#000', fontWeight: 700, cursor: 'pointer', fontSize: '.85rem' }}>
            <UserPlus size={15} /> Add person
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.4)', color: '#ef4444', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 14, fontSize: '.85rem', display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--hp-dim)', padding: 40, textAlign: 'center' }}>Loading night-shift staff…</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={{ ...th, cursor: 'default' }}>Photo</th>
                {COLS.map((c) => (
                  <th key={c.key} style={th} onClick={() => clickSort(c.key)}>
                    {c.label}{sort.key === c.key ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
                {isAdmin && <th style={{ ...th, cursor: 'default' }}>Reveal</th>}
                {isAdmin && <th style={{ ...th, cursor: 'default' }}>Edit</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const rb = !!busy[r.login];
                const open = !!reveal[r.login];
                return (
                  <React.Fragment key={r.login}>
                    <tr style={{ opacity: r.active ? 1 : 0.55 }}>
                      <td style={td}>
                        <DriverAvatar
                          login={r.login}
                          photo={photos[r.login]}
                          size={34}
                          editable={isAdmin}
                          onUploaded={(url) => setPhotos((p) => ({ ...p, [r.login]: url }))}
                        />
                      </td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{r.login}</td>
                      <td style={td}>{r.name || '—'}</td>
                      <td style={td}>{r.mobile || '—'}</td>
                      <td style={{ ...td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email || '—'}</td>
                      <td style={td}>{r.employment || '—'}</td>
                      <td style={td}>{r.classification || '—'}</td>
                      <td style={td}>{r.facility || '—'}</td>
                      <td style={td}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, fontWeight: 700, fontSize: '.75rem',
                          background: r.permanentNight ? 'rgba(139,92,246,.15)' : 'rgba(120,120,120,.15)', color: r.permanentNight ? '#8b5cf6' : 'var(--hp-dim)' }}>
                          {r.permanentNight ? <Moon size={12} /> : null} {r.permanentNight ? 'Nights' : 'Casual'}
                        </span>
                      </td>
                      <td style={td}>
                        {isAdmin ? (
                          <button onClick={() => toggleActive(r.login, !r.active)} disabled={rb} title={r.active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, border: 'none', cursor: rb ? 'wait' : 'pointer', fontWeight: 700, fontSize: '.75rem',
                              background: r.active ? 'rgba(31,138,76,.15)' : 'rgba(120,120,120,.15)', color: r.active ? 'var(--hp-green)' : 'var(--hp-dim)' }}>
                            {r.active ? <Check size={13} /> : <X size={13} />} {r.active ? 'Active' : 'Inactive'}
                          </button>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700, fontSize: '.75rem', color: r.active ? 'var(--hp-green)' : 'var(--hp-dim)' }}>
                            {r.active ? <Check size={13} /> : <X size={13} />} {r.active ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      {isAdmin && (
                        <td style={td}>
                          <button onClick={() => toggleReveal(r.login)} disabled={rb}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--hp-border)', cursor: 'pointer', fontSize: '.75rem', background: 'transparent', color: 'var(--hp-dim)' }}>
                            {open ? <EyeOff size={13} /> : <Eye size={13} />} {open ? 'Hide' : 'Reveal'}
                          </button>
                        </td>
                      )}
                      {isAdmin && (
                        <td style={td}>
                          <button onClick={() => setEditing(r)} disabled={rb} title="Edit record / PIN"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--hp-border)', cursor: 'pointer', fontSize: '.75rem', background: 'transparent', color: 'var(--hp-dim)' }}>
                            <KeyRound size={13} /> Edit
                          </button>
                        </td>
                      )}
                    </tr>
                    {isAdmin && open && <RevealPanel sorter={r} colSpan={COLS.length + 1 + extraCols} by={currentUser?.login || 'admin'} />}
                  </React.Fragment>
                );
              })}
              {!filtered.length && (
                <tr><td colSpan={COLS.length + 1 + extraCols} style={{ ...td, textAlign: 'center', color: 'var(--hp-dim)', padding: 30 }}>No night-shift staff match “{query}”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && editing && (
        <EditModal
          sorter={editing}
          onClose={() => setEditing(null)}
          onSaved={(login, patch) => { patchRow(login, patch); setReveal({}); }}
        />
      )}
      {isAdmin && adding && (
        <AddModal onClose={() => setAdding(false)} onAdded={load} />
      )}
    </div>
  );
}
